import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, AlertTriangle, Sparkles, QrCode } from "lucide-react";
import { formatMoney } from "@/lib/money";

type ScanResult = {
  status: "rewarded" | "already_scanned";
  hunt: { id: string; title: string };
  reward: { points: number; cash_cents: number };
  scanned_at?: string;
};

const ERRORS: Record<string, string> = {
  hunt_not_found: "This QR code isn't linked to an active hunt.",
  hunt_inactive: "This hunt is paused.",
  hunt_full: "All rewards for this hunt have been claimed.",
  own_hunt: "You can't scan your own hunt.",
  unauthorized: "Sign in to claim this reward.",
  missing_code: "Invalid QR code.",
};

const HuntScan = () => {
  const { code = "" } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { session, loading: authLoading } = useAuth();
  const [state, setState] = useState<"loading" | "ok" | "error">("loading");
  const [result, setResult] = useState<ScanResult | null>(null);
  const [errorKey, setErrorKey] = useState<string>("");

  useEffect(() => {
    if (authLoading) return;
    if (!session) {
      navigate(`/auth?next=/hunt/${code}`);
      return;
    }
    (async () => {
      const { data, error } = await supabase.functions.invoke("qr-scan", { body: { code } });
      if (error) {
        setErrorKey("network");
        setState("error");
        return;
      }
      if (data?.error) {
        setErrorKey(data.error);
        setState("error");
        return;
      }
      setResult(data as ScanResult);
      setState("ok");
    })();
  }, [code, session, authLoading, navigate]);

  return (
    <MobileShell>
      <header className="bg-gradient-hero text-primary-foreground px-5 pt-10 pb-12 text-center">
        <div className="h-16 w-16 mx-auto rounded-2xl bg-white/15 grid place-items-center backdrop-blur-sm">
          <QrCode className="h-8 w-8" />
        </div>
        <h1 className="font-display text-2xl font-bold mt-4">Hunt Scan</h1>
      </header>

      <div className="px-5 -mt-6 flex-1">
        <div className="rounded-2xl bg-background shadow-elegant p-6 text-center">
          {state === "loading" && (
            <>
              <Loader2 className="h-10 w-10 animate-spin mx-auto text-primary" />
              <p className="text-sm text-muted-foreground mt-4">Validating your scan…</p>
            </>
          )}

          {state === "ok" && result && (
            <>
              {result.status === "rewarded" ? (
                <>
                  <div className="h-16 w-16 mx-auto rounded-full bg-emerald-500/15 grid place-items-center text-emerald-600 animate-scale-in">
                    <Sparkles className="h-8 w-8" />
                  </div>
                  <p className="font-display text-xl font-bold mt-3">Reward unlocked!</p>
                </>
              ) : (
                <>
                  <div className="h-16 w-16 mx-auto rounded-full bg-muted grid place-items-center text-muted-foreground">
                    <CheckCircle2 className="h-8 w-8" />
                  </div>
                  <p className="font-display text-xl font-bold mt-3">Already claimed</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    You scanned this hunt earlier.
                  </p>
                </>
              )}
              <p className="text-sm text-muted-foreground mt-2">"{result.hunt.title}"</p>

              <div className="grid grid-cols-2 gap-3 mt-5">
                <div className="rounded-xl bg-muted/40 p-3">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Points</p>
                  <p className="font-display text-2xl font-bold">+{result.reward.points}</p>
                </div>
                <div className="rounded-xl bg-muted/40 p-3">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Cash</p>
                  <p className="font-display text-2xl font-bold">
                    {result.reward.cash_cents > 0 ? formatMoney(result.reward.cash_cents) : "—"}
                  </p>
                </div>
              </div>

              <div className="flex gap-2 mt-5">
                <Button asChild variant="outline" className="flex-1">
                  <Link to="/points">View points</Link>
                </Button>
                <Button asChild className="flex-1">
                  <Link to="/wallet">Wallet</Link>
                </Button>
              </div>
            </>
          )}

          {state === "error" && (
            <>
              <div className="h-16 w-16 mx-auto rounded-full bg-amber-500/15 grid place-items-center text-amber-600">
                <AlertTriangle className="h-8 w-8" />
              </div>
              <p className="font-display text-lg font-bold mt-3">Couldn't claim</p>
              <p className="text-sm text-muted-foreground mt-1">
                {ERRORS[errorKey] ?? "Something went wrong. Try again."}
              </p>
              <Button asChild variant="outline" className="w-full mt-5">
                <Link to="/wallet">Back to wallet</Link>
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="safe-bottom" />
    </MobileShell>
  );
};

export default HuntScan;
